import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import useElementSize from "../hooks/useElementSize";
import useWindowSize from "../hooks/useWindowSize";
import { ACTIVE_HEADER_VARIANT, HEADER_VARIANTS } from "../config/headerTheme";

const BottomBarContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const IconWrapper = styled.div`
  position: relative;
  display: inline-block;
  line-height: 0;
`;

const Badge = styled.span`
  position: absolute;
  top: -4px;
  right: -4px;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #ff3b30;
  border: 1.5px solid #fff;
  box-sizing: content-box;
  z-index: 1;
`;

const TabContent = styled.div`
  width: 100%;
  box-sizing: border-box;
  padding: 0 15px 110px;
  text-align: center;
  font-size: 18px;
  overflow: hidden;
`;

const BottomBarStyled = styled.div`
  --header-primary: ${() => HEADER_VARIANTS[ACTIVE_HEADER_VARIANT].primary};
  --header-accent: ${() => HEADER_VARIANTS[ACTIVE_HEADER_VARIANT].accent};
  --header-inactive: ${() => HEADER_VARIANTS[ACTIVE_HEADER_VARIANT].inactive};
  --header-divider: ${() => HEADER_VARIANTS[ACTIVE_HEADER_VARIANT].divider};

  position: fixed;
  bottom: 0;
  width: 100%;
  background: #ffffff;
  display: flex;
  justify-content: space-around;
  align-items: center;
  padding: 10px 0;
  box-shadow: 0px -2px 10px rgba(0, 0, 0, 0.1);
  border-top: 2px solid var(--header-divider);
`;

const TabItem = styled.button`
  position: relative;
  z-index: ${(props) => (props.$hasBadge ? 2 : 1)};
  background: none;
  border: none;
  text-align: center;
  flex: 1;
  min-width: 0;
  padding: 10px 4px;
  font-size: 18px;
  color: ${(props) => (props.$active ? "var(--header-accent)" : "var(--header-inactive)")};
  cursor: pointer;
  transition: 0.3s;
  outline: 0 !important;
  transform: ${(props) => (props.$active ? "scale(1.05)" : "scale(1)")};

  span {
    display: block;
    font-size: 11px;
    margin-top: 3px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 100%;
  }

  &:active {
    transform: scale(0.95);
  }

  svg path {
    stroke: ${(props) => (props.$active ? "var(--header-accent)" : "var(--header-inactive)")};
  }

  @media (max-width: 480px) {
    font-size: 16px;
    padding: 10px 2px;
    span {
      font-size: 9px;
    }
  }
`;

const BottomBar = ({
    id = "bottom-bar",
    autoHeight = true,
    headerSize = { width: 0, height: 0 },
    activeTab,
    setActiveTab,
    tabs,
    navigationTabs,
    active = 0,
    onTab = null,
    loader = false,
    onTabHeight = () => { },
}) => {
    const tabContainer = useRef(null);
    const bottomTabContainer = useRef(null);
    const { width: tw, height: th } = useElementSize(tabContainer);
    const { width: bw, height: bh } = useElementSize(bottomTabContainer);
    const { width, height } = useWindowSize();
    const [tabHeight, setTabHeight] = useState(0);

    useEffect(() => {
        if (autoHeight) {
            const tools = headerSize.height + bh;
            const total = height - tools;
            setTabHeight(total);
            onTabHeight(total);
        } else {
            setTabHeight("auto");
            onTabHeight("auto");
        }
    }, [tw, th, bw, bh, headerSize]);

    const options = {}

    useEffect(() => {
        if (!onTab || !Array.isArray(tabs)) return;
        const currentTab = tabs.find((e) => e?.id === activeTab);
        if (currentTab) onTab(currentTab);
    }, [activeTab, tabs, onTab])

    const allTabs = Array.isArray(tabs) ? tabs : [];
    const navTabs = Array.isArray(navigationTabs) ? navigationTabs : allTabs;
    const activeTabId = allTabs.some((tab) => tab.id === activeTab)
        ? activeTab
        : allTabs?.[0]?.id;

    return (
        <BottomBarContainer id={id}>
            <TabContent ref={tabContainer} style={options}>
                {tabs.map((tab) =>
                    tab.id === activeTabId ? <div key={tab.id}>{tab.component}</div> : null
                )}
            </TabContent>

            <BottomBarStyled ref={bottomTabContainer}>
                {navTabs.map((tab, i) => (
                    <TabItem
                        key={tab.id}
                        $active={activeTabId === tab.id}
                        $hasBadge={!!tab.badge}
                        onClick={() => {
                            if (loader) return;
                            if (tab?.onClick && !tab?.component) {
                                tab.onClick();
                                return;
                            }
                            if (onTab) onTab(tab);
                            setActiveTab(tab.id);
                        }}
                    >
                        <IconWrapper>
                            {tab.icon}
                            {tab.badge && <Badge />}
                        </IconWrapper>
                        {tab.label && <span>{tab.label}</span>}
                    </TabItem>
                ))}
            </BottomBarStyled>
        </BottomBarContainer>
    );
};

export const TabContentComponent = ({ tabs, activeTab }) => {
    return (
        <TabContent>
            {tabs.map((tab) =>
                tab.id === activeTab ? <div key={tab.id}>{tab.component}</div> : null
            )}
        </TabContent>
    );
};

export const BottomNavigation = ({ tabs, activeTab, onChangeTab, loader }) => {
    return (
        <BottomBarStyled>
            {tabs.map((tab) => (
                <TabItem
                    key={tab.id}
                    $active={activeTab === tab.id}
                    $hasBadge={!!tab.badge}
                    onClick={() => {
                        if (!loader) {
                            if (onChangeTab) onChangeTab(tab);
                        }
                    }}
                >
                    <IconWrapper>
                        {tab.icon}
                        {tab.badge && <Badge />}
                    </IconWrapper>
                    <span>{tab.label}</span>
                </TabItem>
            ))}
        </BottomBarStyled>
    );
};

export default BottomBar;
